import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../config/firebase';
import { signInWithPopup, signInWithRedirect, getRedirectResult, GoogleAuthProvider, signOut, onAuthStateChanged, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { sessionManager } from '../utils/sessionManager';

const AuthContext = createContext();

// 실제 인앱 브라우저만 감지 (정상 모바일 브라우저 허용)
const isInAppBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Google OAuth에서 실제로 차단하는 인앱 브라우저만 감지
  const isRealInAppBrowser = (
    /FBAN|FBAV/i.test(userAgent) || // Facebook 앱
    /Instagram/i.test(userAgent) || // Instagram 앱
    /KAKAOTALK/i.test(userAgent) || // 카카오톡 앱
    /LINE/i.test(userAgent) || // 라인 앱
    /WeChat/i.test(userAgent) || // 위챗 앱
    /Twitter/i.test(userAgent) || // 트위터 앱
    /WhatsApp/i.test(userAgent) || // 왓츠앱 앱
    userAgent.includes('GSA/') || // Google Search App
    // Android WebView 더 정확한 감지
    (userAgent.includes('wv') && userAgent.includes('Version') && !userAgent.includes('Chrome'))
  );
  
  console.log('🔍 인앱브라우저 감지:', {
    userAgent: userAgent,
    isRealInAppBrowser: isRealInAppBrowser
  });
  
  return isRealInAppBrowser;
};

// 단순한 모바일 감지 (팝업 지원 여부 확인용)
const isMobile = () => {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Firebase Auth 지속성 설정 (모바일에서 중요)
  useEffect(() => {
    const setupAuth = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log('✅ Firebase Auth 지속성 설정 완료 (LOCAL)');
      } catch (error) {
        console.warn('⚠️ Firebase Auth 지속성 설정 실패:', error.message);
      }
    };
    
    setupAuth();
  }, []);

  // Google OAuth Provider 설정
  const googleProvider = new GoogleAuthProvider();
  googleProvider.addScope('email');
  googleProvider.addScope('profile');
  
  // 모든 환경에서 기본 설정 사용 (모바일/데스크톱 구분 제거)
  googleProvider.setCustomParameters({
    prompt: 'select_account'
  });

  // 사용자 정보를 Firestore에 저장/업데이트
  const saveUserToFirestore = async (user) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        lastLoginAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (userDoc.exists()) {
        // 기존 사용자 정보 업데이트
        await updateDoc(userRef, userData);
      } else {
        // 신규 사용자 정보 저장
        await setDoc(userRef, {
          ...userData,
          createdAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('사용자 정보 저장 실패:', error);
    }
  };

  // Google 로그인 (단순화된 로직)
  const loginWithGoogle = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let result;
      const isInApp = isInAppBrowser();
      const mobile = isMobile();
      
      console.log('🔐 로그인 시도 시작:', {
        isInApp: isInApp,
        isMobile: mobile,
        userAgent: navigator.userAgent,
        currentURL: window.location.href
      });
      
      // 인앱 브라우저에서만 리다이렉트 강제, 일반 모바일 브라우저는 팝업 시도
      if (isInApp) {
        console.log('📱 인앱브라우저 감지 - 리다이렉트 방식 사용');
        await signInWithRedirect(auth, googleProvider);
        return null; // 리다이렉트는 페이지가 새로고침되므로 null 반환
      } else {
        // 일반 브라우저 (모바일 포함)에서는 팝업 시도, 실패시 리다이렉트
        console.log('🌐 일반 브라우저 감지 - 팝업 방식 시도');
        try {
          result = await signInWithPopup(auth, googleProvider);
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ 팝업 로그인 성공');
          }
        } catch (popupError) {
          // 팝업이 실패한 경우에만 리다이렉트로 fallback
          if (popupError.code === 'auth/popup-blocked' || 
              popupError.code === 'auth/popup-closed-by-user' ||
              popupError.code === 'auth/cancelled-popup-request') {
            console.log('🚫 팝업 실패, 리다이렉트로 전환:', popupError.code);
            await signInWithRedirect(auth, googleProvider);
            return null;
          } else {
            throw popupError;
          }
        }
      }
      
      const user = result.user;
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ 로그인 성공');
      }
      
      // 세션 시작
      await sessionManager.startSession(user);
      
      // Firestore에 사용자 정보 저장
      await saveUserToFirestore(user);
      
      return user;
    } catch (error) {
      console.error('❌ Google 로그인 실패:', error);
      
      // 구체적인 에러 메시지 제공
      if (error.code === 'auth/popup-blocked') {
        setError('팝업이 차단되었습니다. 팝업을 허용하거나 페이지를 새로고침해주세요.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        setError('로그인이 취소되었습니다.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('인증되지 않은 도메인입니다. 관리자에게 문의해주세요.');
      } else if (error.code === 'auth/operation-not-allowed') {
        setError('Google 로그인이 비활성화되어 있습니다.');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setError('이미 다른 방법으로 가입된 계정입니다.');
      } else if (error.message && error.message.includes('disallowed_useragent')) {
        setError('현재 브라우저에서는 로그인할 수 없습니다. 기본 브라우저(Chrome, Safari 등)에서 시도해주세요.');
      } else {
        setError('로그인 실패: ' + (error.message || '알 수 없는 오류'));
      }
      
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 로그아웃
  const logout = async () => {
    setLoading(true);
    setError(null);

    try {
      await signOut(auth);
      await sessionManager.endSession();
      setUser(null);

      // 암호화 salt 캐시 초기화
      const { clearSaltCache } = await import('../services/userKeyService');
      clearSaltCache();
    } catch (error) {
      console.error('로그아웃 실패:', error);
      setError('로그아웃 실패: ' + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 인증 상태 변화 감지 및 리다이렉트 결과 처리 (단순화)
  useEffect(() => {
    let isMounted = true;
    
    // 리다이렉트 결과 처리
    const handleRedirectResult = async () => {
      try {
        setLoading(true);
        console.log('🔄 리다이렉트 결과 확인 중...');
        
        // 추가 대기 시간 (Firebase 초기화 완료 대기)
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const result = await getRedirectResult(auth);
        
        console.log('📋 getRedirectResult 결과:', {
          hasResult: !!result,
          hasUser: !!result?.user,
          userEmail: result?.user?.email || 'none'
        });
        
        if (result && result.user && isMounted) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🎉 리다이렉트 로그인 성공!');
          }
          
          setUser(result.user);
          await sessionManager.startSession(result.user);
          
          // Firestore에 사용자 정보 저장
          try {
            await saveUserToFirestore(result.user);
            console.log('💾 사용자 정보 Firestore 저장 완료');
          } catch (error) {
            console.warn('⚠️ 사용자 정보 저장 실패 (계속 진행):', error.message);
          }
          
          if (isMounted) {
            setLoading(false);
          }
          return;
        }
      } catch (error) {
        console.error('❌ 리다이렉트 결과 처리 실패:', error);
        if (isMounted) {
          if (error.message && error.message.includes('disallowed_useragent')) {
            setError('현재 브라우저에서는 로그인할 수 없습니다. 기본 브라우저(Chrome, Safari 등)로 접속해주세요.');
          } else {
            setError('로그인 처리 중 오류가 발생했습니다: ' + (error.message || '알 수 없는 오류'));
          }
          setLoading(false);
        }
        return;
      }
      
      // 일반 auth state 감지
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (!isMounted) return;
        
        console.log('🔐 onAuthStateChanged:', {
          hasUser: !!firebaseUser,
          userEmail: firebaseUser?.email || 'none'
        });
        
        setLoading(true);
        
        if (firebaseUser) {
          // 중복 처리 방지
          if (user && user.uid === firebaseUser.uid) {
            console.log('ℹ️ 동일한 사용자 - 중복 처리 방지');
            setLoading(false);
            return;
          }
          
          setUser(firebaseUser);
          await sessionManager.startSession(firebaseUser);
          
          // Firestore 사용자 정보 업데이트
          try {
            await saveUserToFirestore(firebaseUser);
            console.log('💾 onAuthStateChanged - 사용자 정보 저장 완료');
          } catch (error) {
            console.warn('⚠️ 사용자 정보 저장 실패 (계속 진행):', error.message);
          }
        } else {
          setUser(null);
          await sessionManager.endSession();
        }
        
        if (isMounted) {
          setLoading(false);
        }
      });

      return () => {
        isMounted = false;
        unsubscribe();
      };
    };
    
    const cleanup = handleRedirectResult();
    
    return async () => {
      isMounted = false;
      if (cleanup && typeof cleanup.then === 'function') {
        const cleanupFn = await cleanup;
        if (cleanupFn && typeof cleanupFn === 'function') {
          cleanupFn();
        }
      }
    };
  }, []);

  // 세션 만료 체크
  useEffect(() => {
    if (!user) return; // 사용자가 없으면 체크하지 않음
    
    const checkSession = async () => {
      if (user && await sessionManager.isSessionExpired()) {
        console.log('⏰ 세션 만료로 자동 로그아웃');
        logout();
      }
    };

    // 5분마다 세션 체크
    const interval = setInterval(checkSession, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user]); // user가 변경될 때만 실행

  const value = {
    user,
    loading,
    error,
    loginWithGoogle,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
export { AuthContext };
